import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtAUD, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import FunnelChart from "@/components/FunnelChart";
import DateRangePicker from "@/components/DateRangePicker";
import MyGoalsCard from "@/components/MyGoalsCard";
import ActivitiesWeekCard from "@/components/ActivitiesWeekCard";
import { Pill, TEMP_STYLES } from "@/components/badges";
import { TrendingUp, Users, Briefcase, Target, Trophy, Activity, Clock, ArrowUpRight, UserCheck, Handshake } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#29ABE2", "#1B4F8B", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#0EA5E9", "#64748B", "#475569"];

function Kpi({ label, value, sub, icon: Icon, tone = "primary", to }) {
  const tones = {
    primary: { bg: "rgba(41, 171, 226, 0.10)", ring: "rgba(41, 171, 226, 0.20)", ink: "#1f6f9e" },
    success: { bg: "var(--eyb-emerald-soft)", ring: "rgba(15, 143, 91, 0.18)", ink: "#0F8F5B" },
    warn: { bg: "var(--eyb-amber-soft)", ring: "rgba(181, 122, 31, 0.20)", ink: "#B57A1F" },
    danger: { bg: "var(--eyb-rose-soft)", ring: "rgba(180, 50, 94, 0.18)", ink: "#B4325E" },
  };
  const t = tones[tone] || tones.primary;
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="eyb-label">{label}</div>
          <div className="mt-3 eyb-kpi-value" style={{ fontSize: "32px" }}>{value}</div>
          {sub && <div className="text-[12px] mt-1.5" style={{ color: "var(--eyb-ink-3)" }}>{sub}</div>}
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{
            background: t.bg,
            boxShadow: `inset 0 0 0 1px ${t.ring}`,
            color: t.ink,
          }}
        >
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
      </div>
      {to && (
        <div
          className="text-[11px] mt-4 inline-flex items-center gap-1 group-hover:gap-2 transition-all"
          style={{ color: t.ink, letterSpacing: "0.02em", fontWeight: 550 }}
        >
          View details <ArrowUpRight className="w-3 h-3" />
        </div>
      )}
    </>
  );
  const cls = `eyb-card eyb-card-hover p-5 block text-left ${to ? "group cursor-pointer" : ""}`;
  if (to) return <Link to={to} className={cls} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</Link>;
  return <div className={cls} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</div>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  useEffect(() => {
    const params = {};
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    setData(null);
    api.get("/dashboard/summary", { params }).then((r) => setData(r.data)).catch(() => setData({}));
  }, [dateRange.from, dateRange.to]);

  const qs = () => {
    const p = new URLSearchParams();
    if (dateRange.from) p.set("date_from", dateRange.from);
    if (dateRange.to) p.set("date_to", dateRange.to);
    return p.toString();
  };
  const leadsLink = (extra = {}) => {
    const p = new URLSearchParams(qs());
    Object.entries(extra).forEach(([k, v]) => v && p.set(k, v));
    return `/leads?${p.toString()}`;
  };
  const oppsLink = (extra = {}) => {
    const p = new URLSearchParams(qs());
    Object.entries(extra).forEach(([k, v]) => v && p.set(k, v));
    return `/opportunities?${p.toString()}`;
  };

  if (!data) return <div className="p-8 text-slate-400">Loading dashboard…</div>;
  const t = data.totals || {};
  const sourceData = Object.entries(data.by_source || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v }));
  const tempData = Object.entries(data.by_temperature || {}).map(([k, v]) => ({ name: k, value: v }));
  const canSeeTeam = user?.role === "admin" || user?.role === "full_access";

  return (
    <div className="p-8 space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyb-label">Executive Dashboard</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Welcome back, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-sm text-slate-500 mt-1">Pipeline performance for the selected period.</p>
        </div>
        <DateRangePicker value={dateRange} onChange={setDateRange} defaultPreset="fy" />
      </div>

      {/* KPIs — clickable */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Active Leads" value={t.active_leads ?? 0} icon={Users} to={leadsLink({ status: "New" })} />
        <Kpi label="Active Opportunities" value={t.active_opportunities ?? 0} icon={Briefcase} tone="success" to={oppsLink({ active: "1" })} />
        <Kpi label="Pipeline Value" value={fmtAUD(t.total_pipeline)} sub={`Weighted: ${fmtAUD(t.weighted_pipeline)}`} icon={TrendingUp} to={oppsLink()} />
        <Kpi label="Closed Won" value={fmtAUD(t.closed_won_value)} sub={`${t.closed_won ?? 0} deals won`} icon={Trophy} tone="warn" to={oppsLink({ stage: "Closed Won" })} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="eyb-card p-5">
          <div className="eyb-label">Lead → Opportunity</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{t.lead_to_opp_rate ?? 0}%</div>
          <div className="w-full h-2 bg-slate-100 rounded-full mt-3 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#29ABE2] to-[#1B4F8B]" style={{ width: `${Math.min(100, t.lead_to_opp_rate || 0)}%` }} />
          </div>
        </div>
        <div className="eyb-card p-5">
          <div className="eyb-label">Opportunity → Closed Won</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{t.opp_to_won_rate ?? 0}%</div>
          <div className="w-full h-2 bg-slate-100 rounded-full mt-3 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-700" style={{ width: `${Math.min(100, t.opp_to_won_rate || 0)}%` }} />
          </div>
        </div>
        <Link to={oppsLink({ stage: "Closed Lost" })} className="eyb-card eyb-card-hover p-5 block group" data-testid="kpi-closed-lost">
          <div className="eyb-label">Closed Lost</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{t.closed_lost ?? 0}</div>
          <div className="text-xs text-slate-500 mt-3">Opportunities not won this period</div>
        </Link>
      </div>

      {/* My Goals — visible to everyone */}
      <MyGoalsCard />

      {/* Activities this week — leaderboard for admin, personal count for members */}
      <ActivitiesWeekCard />

      {/* Funnel + Temperature/Source */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="eyb-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="eyb-label">Opportunity Funnel</div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mt-1">Sales Pipeline</h3>
            </div>
            <Target className="w-5 h-5 text-slate-400" />
          </div>
          <div className="space-y-1">
            {(data.funnel || []).map((s) => (
              <Link key={s.stage} to={oppsLink({ stage: s.stage })} className="block hover:bg-slate-50/60 -mx-2 px-2 py-1 rounded-md transition-colors" data-testid={`funnel-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}>
                <FunnelRow s={s} max={Math.max(1, ...(data.funnel || []).map((x) => x.count))} idx={(data.funnel || []).indexOf(s)} total={(data.funnel || []).length} />
              </Link>
            ))}
          </div>
        </div>

        <div className="eyb-card p-6">
          <div className="eyb-label mb-4">Leads by Temperature</div>
          <div className="space-y-3">
            {tempData.map((t) => (
              <Link key={t.name} to={leadsLink({ temperature: t.name })} className="flex items-center gap-3 hover:bg-slate-50 -mx-2 px-2 py-1 rounded-md transition-colors" data-testid={`temp-${t.name.toLowerCase()}`}>
                <Pill className={TEMP_STYLES[t.name]}>{t.name}</Pill>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full" style={{
                    width: `${Math.min(100, (t.value / Math.max(1, ...tempData.map((x) => x.value))) * 100)}%`,
                    background: t.name === "Hot" ? "#EF4444" : t.name === "Warm" ? "#F59E0B" : "#64748B",
                  }} />
                </div>
                <span className="text-sm font-semibold text-slate-700 w-8 text-right">{t.value}</span>
              </Link>
            ))}
          </div>

          <div className="eyb-label mt-6 mb-2">Leads by Source</div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={sourceData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  onClick={(d) => navigate(leadsLink({ source: d?.name }))}
                  style={{ cursor: "pointer" }}
                >
                  {sourceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {sourceData.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {sourceData.map((s, i) => (
                <Link key={s.name} to={leadsLink({ source: s.name })} className="inline-flex items-center gap-1 text-[10px] text-slate-600 hover:text-slate-900">
                  <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  {s.name} <span className="font-semibold">({s.value})</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Leads by Team Member — admin/full_access only */}
      {canSeeTeam && (
        <div className="eyb-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-slate-400" />
              <div>
                <div className="eyb-label">Leads by Team Member</div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mt-1">Pipeline ownership</h3>
              </div>
            </div>
            <Link to={leadsLink()} className="text-xs text-[#1f8ebf] hover:underline flex items-center gap-1">View all leads <ArrowUpRight className="w-3 h-3" /></Link>
          </div>
          {(data.by_assignee || []).length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center">No assignments in this period.</div>
          ) : (
            <>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={data.by_assignee || []} onClick={(d) => { if (d?.activePayload?.[0]?.payload?.user_id) navigate(leadsLink({ assigned_to: d.activePayload[0].payload.user_id })); }}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                    <Bar dataKey="count" fill="#29ABE2" radius={[6, 6, 0, 0]} style={{ cursor: "pointer" }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
                {(data.by_assignee || []).map((a) => (
                  <Link key={a.user_id} to={leadsLink({ assigned_to: a.user_id })} className="flex items-center justify-between px-3 py-2 rounded-md border border-slate-100 hover:border-[#29ABE2]/40 hover:bg-slate-50/50 transition-colors" data-testid={`assignee-${a.user_id}`}>
                    <span className="text-sm font-medium text-slate-800">{a.name}</span>
                    <span className="text-xs font-bold text-[#1f8ebf]">{a.count} leads</span>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Leads by Partnership */}
      <div className="eyb-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Handshake className="w-4 h-4 text-slate-400" />
            <div>
              <div className="eyb-label">Leads by Partnership</div>
              <h3 className="font-heading text-lg font-semibold text-slate-900 mt-1">Referral channel performance</h3>
            </div>
          </div>
          <Link to={leadsLink()} className="text-xs text-[#1f8ebf] hover:underline flex items-center gap-1">View all leads <ArrowUpRight className="w-3 h-3" /></Link>
        </div>
        {(data.by_partnership || []).length === 0 ? (
          <div className="text-sm text-slate-400 py-8 text-center">No leads tagged with a partnership in this period.</div>
        ) : (
          <>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart layout="vertical" data={data.by_partnership || []} onClick={(d) => { if (d?.activePayload?.[0]?.payload?.name) navigate(leadsLink({ partnership: d.activePayload[0].payload.name })); }} margin={{ left: 20, right: 30 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} formatter={(v) => [`${v} leads`, "Count"]} />
                  <Bar dataKey="count" fill="#1B4F8B" radius={[0, 6, 6, 0]} style={{ cursor: "pointer" }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
              {(data.by_partnership || []).map((p) => (
                <Link key={p.name} to={leadsLink({ partnership: p.name })} className="flex items-center justify-between px-3 py-2 rounded-md border border-slate-100 hover:border-[#29ABE2]/40 hover:bg-slate-50/50 transition-colors" data-testid={`partnership-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="text-sm font-medium text-slate-800">{p.name}</span>
                  <span className="text-xs font-bold text-[#1f8ebf]">{p.count} leads</span>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Opportunities Pipeline by Team Member — admin/full_access only */}
      {canSeeTeam && (
        <div className="eyb-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-slate-400" />
              <div>
                <div className="eyb-label">Opportunities Pipeline by Team Member</div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mt-1">Deals & pipeline value</h3>
              </div>
            </div>
            <Link to={oppsLink()} className="text-xs text-[#1f8ebf] hover:underline flex items-center gap-1">View all opportunities <ArrowUpRight className="w-3 h-3" /></Link>
          </div>
          {(data.opps_by_assignee || []).length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center">No opportunities in this period.</div>
          ) : (
            <>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={data.opps_by_assignee || []} onClick={(d) => { if (d?.activePayload?.[0]?.payload?.user_id) navigate(oppsLink({ assigned_to: d.activePayload[0].payload.user_id })); }}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                      formatter={(v, name) => [fmtAUD(v), name]}
                    />
                    <Bar dataKey="pipeline_value" name="Pipeline" stackId="a" fill="#29ABE2" radius={[0, 0, 0, 0]} style={{ cursor: "pointer" }} />
                    <Bar dataKey="won_value" name="Won" stackId="a" fill="#10B981" radius={[6, 6, 0, 0]} style={{ cursor: "pointer" }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                      <th className="py-2 font-semibold">Team Member</th>
                      <th className="py-2 font-semibold text-right">Opps</th>
                      <th className="py-2 font-semibold text-right">Pipeline</th>
                      <th className="py-2 font-semibold text-right">Weighted</th>
                      <th className="py-2 font-semibold text-right">Won</th>
                      <th className="py-2 font-semibold text-right">Won Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.opps_by_assignee || []).map((a) => (
                      <tr key={a.user_id} className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer" onClick={() => navigate(oppsLink({ assigned_to: a.user_id }))} data-testid={`opps-assignee-${a.user_id}`}>
                        <td className="py-2.5 font-medium text-slate-800">{a.name}</td>
                        <td className="py-2.5 text-right text-slate-700">{a.count}</td>
                        <td className="py-2.5 text-right font-semibold text-slate-900">{fmtAUD(a.pipeline_value)}</td>
                        <td className="py-2.5 text-right text-slate-600">{fmtAUD(a.weighted_value)}</td>
                        <td className="py-2.5 text-right text-slate-700">{a.won_count}</td>
                        <td className="py-2.5 text-right font-semibold text-emerald-600">{fmtAUD(a.won_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="eyb-card p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-slate-400" />
            <div className="eyb-label">Upcoming Tasks & Follow-ups</div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="py-2 font-semibold">Task</th>
                <th className="py-2 font-semibold">Priority</th>
                <th className="py-2 font-semibold">Due</th>
                <th className="py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data.upcoming_tasks || []).map((tk) => (
                <tr key={tk.id} className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer" onClick={() => navigate("/tasks")}>
                  <td className="py-2.5 font-medium text-slate-800">{tk.title}</td>
                  <td className="py-2.5">
                    <Pill className={tk.priority === "High" ? "bg-rose-50 text-rose-700 border border-rose-200" : tk.priority === "Medium" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-100 text-slate-600 border border-slate-200"}>{tk.priority}</Pill>
                  </td>
                  <td className="py-2.5 text-slate-600">{tk.due_date || "—"}</td>
                  <td className="py-2.5 text-slate-600">{tk.status}</td>
                </tr>
              ))}
              {(data.upcoming_tasks || []).length === 0 && (
                <tr><td colSpan={4} className="py-4 text-center text-slate-400 text-xs">No upcoming tasks.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="eyb-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-slate-400" />
            <div className="eyb-label">Recent Activity</div>
          </div>
          <ul className="space-y-3 max-h-80 overflow-y-auto" data-testid="recent-activity">
            {(data.recent_activity || []).slice(0, 10).map((a) => (
              <li key={a.id} className="text-xs border-l-2 border-[#29ABE2]/40 pl-3">
                <div className="font-semibold text-slate-700 capitalize">{a.user_name} <span className="font-normal text-slate-500">{a.action} {a.entity_type}</span></div>
                <div className="text-[11px] text-slate-400 mt-0.5">{fmtDateTime(a.timestamp)}</div>
              </li>
            ))}
            {(data.recent_activity || []).length === 0 && <li className="text-xs text-slate-400">No activity yet.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function FunnelRow({ s, max, idx, total }) {
  const widthPct = 30 + (s.count / max) * 70;
  const STAGE_COLORS = {
    "Need Assessment":     { from: "#38BDF8", to: "#0284C7" },  // sky
    "Formal Presentation": { from: "#818CF8", to: "#4338CA" },  // indigo
    "Decision Pending":    { from: "#F59E0B", to: "#B45309" },  // amber
    "Closed Won":          { from: "#34D399", to: "#047857" },  // emerald
    "Closed Lost":         { from: "#FB7185", to: "#BE123C" },  // rose
  };
  const c = STAGE_COLORS[s.stage] || { from: "#29ABE2", to: "#1B4F8B" };
  return (
    <div className="funnel-row py-1.5">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: c.from }} />
          <span className="text-xs font-semibold text-slate-700">{s.stage}</span>
          <span className="text-[11px] text-slate-500">{s.count} opp{s.count === 1 ? "" : "s"}</span>
        </div>
        <span className="text-xs font-bold text-slate-900">{fmtAUD(s.value)}</span>
      </div>
      <div className="relative h-9 flex">
        <div
          className="h-full rounded-md flex items-center justify-end pr-4 transition-all duration-500"
          style={{
            width: `${widthPct}%`,
            background: `linear-gradient(90deg, ${c.from}, ${c.to})`,
            clipPath: idx < total - 1 ? "polygon(0 0, 100% 0, 96% 100%, 0 100%)" : undefined,
          }}
        >
          <span className="text-white text-xs font-bold drop-shadow-sm">
            {fmtAUD(s.weighted)} <span className="opacity-80 font-medium">wtd</span>
          </span>
        </div>
      </div>
    </div>
  );
}
